import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import { emptyStar, fullStar, halfStar } from '@dna/paths'
import { storefrontClient } from '@dna/storefront'
import styles from '@dna/styles'
import {
  getCategoryFromTags,
  getLineItemProperties,
  getMasterSku,
  handleize,
  igGetExperimentVariationName,
  minorToMajorUnit,
  removeTextBeforeCharacterAndTrim,
  renderPrice,
  splitProductName,
  t,
} from '@dna/utils'
import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  repeat,
  when,
} from '@lit'

// Due to shadow DOM restrictions, we can't detect the buttons of the carousel pagination.
const CLOSEST_SELECTORS_OF_IGNORED_CLICKS = 'button, a a, .footer__swatches'
const SELECTORS_OF_IGNORED_CLICKS = '#media-carousel, .footer__swatches'

class DNAProductCard extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    product: {
      type: Object,
    },
    usPricing: {
      type: Boolean,
    },
    imageLoading: {
      type: String,
      attribute: 'image-loading',
    },
    showCarousel: {
      type: Boolean,
      attribute: 'show-carousel',
      reflect: true,
    },
    enableHover: {
      type: Boolean,
      attribute: 'enable-hover-image',
      reflect: true,
    },
    quickViewEnabled: {
      type: Boolean,
      attribute: 'quick-view',
    },
    thumbnailScroll: {
      type: String,
      state: true,
    },
    hideProductColorCount: {
      type: Boolean,
      attribute: 'hide-product-color-count',
    },
    hideProductColorName: {
      type: Boolean,
      attribute: 'hide-product-color-name',
    },
    showEditorialAltImage: {
      type: Boolean,
      attribute: 'show-editorial-alt-image',
      reflect: true,
    },
    hideBadges: {
      type: Boolean,
      attribute: 'hide-badges',
    },
    hideWishlistIcon: {
      type: Boolean,
      attribute: 'hide-wishlist-icon',
    },
    constructorSelectedId: {
      type: String,
    },
    isSearch: {
      type: Boolean,
      attribute: 'is-search',
    },
    sectionId: {
      type: String,
      attribute: 'section-id',
    },
    sectionPosition: {
      type: Number,
      attribute: 'section-position',
    },
    trackingKey: {
      type: String,
      attribute: 'tracking-key',
    },
    selectedWidth: {
      type: String,
      state: true,
    },
    carouselStyle: {
      type: String,
      attribute: 'carousel-style',
    },
    fetchPriority: {
      type: String,
      attribute: 'fetch-priority',
    },
    showReviewStars: {
      type: Boolean,
      attribute: 'show-review-stars',
      reflect: true,
    },
    // @intelligems TECH-1516
    applyTest1516: {
      type: Boolean,
      state: true,
    },
    applyTest1516v2: {
      type: Boolean,
      state: true,
    },
    selectedSwatch: {
      type: String,
      state: true,
    },
    swatchFillCount: {
      type: Number,
      state: true,
    },
    swatchScrollPosition: {
      type: Boolean,
      state: true,
    },
    // END / TECH-1516
  }

  get rawPrice() {
    let rawPrice

    // Data coming from Constructor
    if (this.product.prices) {
      rawPrice = this.product.prices[Shopify.country] * 100
    } else {
      // Data coming from Shopify Storefront API
      rawPrice = this.product.maxPrice
    }

    return rawPrice
  }

  get renderedPrice() {
    return renderPrice(this.rawPrice)
  }

  get renderedComparePrice() {
    if (this.product.compareAtPrices) {
      if (this.product.compareAtPrices[Shopify.country]) {
        this.rawCompareAtPrice =
          this.product.compareAtPrices[Shopify.country] * 100
      }
    } else if (this.product.compareAtPrice) {
      this.rawCompareAtPrice = this.product.compareAtPrice
    }

    if (this.rawPrice >= this.rawCompareAtPrice) {
      return false
    }

    return this.rawCompareAtPrice ? renderPrice(this.rawCompareAtPrice) : false
  }

  get productMedia() {
    // if showEditorialAltImage is true, present all images since we do not know what placement the editorial image is in
    const MAX_IMAGES = this.showEditorialAltImage
      ? this.product.media.length
      : this.isMobile
        ? 2
        : 3
    const result = Object.assign([], this.product.media) // clone to avoid mutating original property

    if (this.hoverIndex !== -1 && this.hoverIndex !== 1) {
      const hoverImage = result.splice(this.hoverIndex, 1)[0]
      result.splice(1, 0, hoverImage)
    }

    const truncated = result.slice(0, MAX_IMAGES)
    return truncated
  }

  get hoverIndex() {
    return this.product.media.findIndex((image) =>
      image.alt.includes(this.hoverFragment),
    )
  }

  get showHoverImage() {
    if (this.isSearch || !this.enableHover || this.compactCard) return false

    let showHoverImage
    const hasHoverImage = this.hoverIndex !== -1
    if (this.productMedia.length > 1 && hasHoverImage) {
      if (this.classList.contains('product-card__product-carousel')) {
        showHoverImage = false
      }
      showHoverImage = true
    }

    return showHoverImage
  }

  get productMediaEmpty() {
    return this.product.media.length === 0
  }

  get productUrl() {
    const constructedUrl = `${window.location.origin}${Shopify.routes.root}products/${this.product.handle}`
    return constructedUrl
  }

  get primaryBadge() {
    // @intelligems TECH-1516
    return this.selectedSwatch?.length
      ? this.product.colorSwatches[this.selectedSwatch]?.primaryBadge
      : this.product.primary_badge
  }

  get primaryBadgeType() {
    return this.product.primary_badge_type?.toLowerCase()
  }

  get secondaryBadge() {
    // @intelligems TECH-1516
    const secondaryBadge = this.selectedSwatch?.length
      ? this.product.colorSwatches[this.selectedSwatch]?.secondaryBadge
      : this.product.secondary_badge
    if (
      (secondaryBadge?.includes('$') && window.Shopify?.country !== 'US') ||
      this.isSearch
    ) {
      return false
    }
    return secondaryBadge
  }

  get maximumOrderQuantity() {
    const limit = this.product.metafields.rothys.maximum_order_quantity
      ? this.product.metafields.rothys.maximum_order_quantity
      : __DNA_GLOBAL__.b2cMaxOrderQuantity
    return limit
  }

  get productTitle() {
    return splitProductName(this?.product?.title)
  }

  get isSingleVariant() {
    return this.product.variants.length === 1
  }

  get isGiftCard() {
    return this?.product?.giftcard
  }

  get colorCount() {
    return this.product?.colorCountByMarket?.[Shopify.country] ?? null
  }

  get editorialImage() {
    const editorialImage = this.productMedia.find((media) =>
      media.alt?.includes('editorial'),
    )
    return this.showEditorialAltImage && editorialImage ? editorialImage : null
  }

  get compactCard() {
    return this.carouselStyle === 'compact'
  }

  $thumbnail = createRef()

  constructor() {
    super()

    this._setMobileBreakpoints(['mobile', 'tablet'])
    this.usPricing = Shopify.country === 'US'
    this.hideProductColorCount = false
    this.hideProductColorName = false
    this.hideBadges = false
    this.hideWishlistIcon = false
    this.hoverFragment = 'hover |'
    this.isSearch = false
    this.sectionId = ''
    this.constructorSelectedId = this.product?.variants?.[0]?.id
    this.thumbnailScroll = 'left'
    this.selectedWidth = 'Medium'
    this.rawCompareAtPrice = 0

    // @intelligems TECH-1516
    this.applyTest1516 = false
    this.applyTest1516v2 = false
    this.selectedSwatch = this.product?.handle || ''
    this.sortedColorSwatchKeys = []
    this.swatchesScrolled = false
    this.swatchesScrolling = false
    this.swatchScrollPosition = 0
    this.swatchFillCount = this.isMobile ? 3 : 4
    this.resizing = false
    // END / TECH-1516
  }

  async connectedCallback() {
    super.connectedCallback()

    // @intelligems TECH-1516
    const tech1516VariationName = await igGetExperimentVariationName({
      staging: '86901113-8306-45d2-94aa-fd00490529bc',
      production: '12c88a7c-1d78-428a-a834-ea7c14801133',
    })
    if (
      tech1516VariationName &&
      tech1516VariationName !== 'Control' &&
      !this.closest('.trending-search-products') // Exclude from header search
    ) {
      // Apply v1
      this.applyTest1516 = true

      // Apply v2
      if (tech1516VariationName.toLowerCase().includes('v2')) {
        this.applyTest1516v2 = true
        window.addEventListener('resize', this._handleResize)
      }

      await this.updateComplete
      this.selectedSwatch = this.product?.handle || ''
      this._setSwatchFillCount()
      this._sortColorSwatchKeys()
    }
    // END / TECH-1516
  }

  // @intelligems TECH-1516
  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('resize', this._handleResize)
  }

  willUpdate(changedProperties) {
    if (this.applyTest1516v2 && changedProperties.has('product')) {
      this._sortColorSwatchKeys()
    }
  }
  // END / TECH-1516

  _handleViewportChange = (newViewport) => {
    super._handleViewportChange(newViewport)
    this._setSwatchFillCount() // @intelligems TECH-1516
    this.requestUpdate()
  }

  _selectConstructorVariant = ({ id }) => {
    this.constructorSelectedId = id ?? this.product?.variants?.[0]?.id
  }

  // @intelligems TECH-1516
  _sortColorSwatchKeys() {
    const arr = Object.keys(this.product?.colorSwatches).sort((a, b) => {
      const aMatches =
        this.product?.colorSwatches[a].handle === this.product?.handle
      const bMatches =
        this.product?.colorSwatches[b].handle === this.product?.handle
      return aMatches === bMatches ? 0 : aMatches ? -1 : 1
    })
    this.sortedColorSwatchKeys = arr.map((item) => {
      return {
        handle: item,
      }
    })
  }

  _handleResize = () => {
    if (!this.resizing) {
      window.requestAnimationFrame(() => {
        this._setSwatchFillCount()
        this.resizing = false
      })
      this.resizing = true
    }
  }

  _setSwatchFillCount = () => {
    if (this.applyTest1516v2) {
      const productCardWidth = this.renderRoot
        ?.querySelector('.js-footer-swatches')
        ?.getBoundingClientRect().width
      const swatchWidth = this.isMobile ? 24 + 16 : 20 + 12 // width plus gap
      this.swatchFillCount = Math.floor(productCardWidth / swatchWidth) - 1 // subtract one to make room for 'next' arrow
    }
  }

  _handleSwatchScroll = (event) => {
    const $footerSwatches = this.renderRoot.querySelector('.js-footer-swatches')
    const $swatches = $footerSwatches?.querySelectorAll('.swatch + label')

    if (event) {
      const direction = event.target.closest('.js-swatch-next')
        ? 'forward'
        : 'backward'

      // Handle scroll distance
      if (direction === 'forward') {
        if (this.swatchesScrolled) {
          this.swatchScrollPosition += this.swatchFillCount - 1
        } else {
          this.swatchScrollPosition += this.swatchFillCount
        }
      } else {
        if (this.swatchesScrolled) {
          if (this.swatchScrollPosition <= this.swatchFillCount) {
            this.swatchScrollPosition = 0
          } else {
            this.swatchScrollPosition -= this.swatchFillCount - 1
          }
        }
      }
    }

    this.swatchesScrolled = true // used to check whether a previous scroll happened
    this.swatchesScrolling = true // indicates that a scroll interaction is happening
    if (this.swatchScrollPosition === 0) {
      this.swatchesScrolled = false
    }
    // Prevents an incidental card click when a scroll button becomes hidden
    setTimeout(() => {
      this.swatchesScrolling = false
    }, 500)
  }

  _handleSwatchClick = (event, handle) => {
    const $card = event.target.closest('.js-product-card-link')
    const $primaryImage = $card?.querySelector(
      '.thumbnail__image--featured dna-image',
    )
    const $secondaryImage = $card?.querySelector(
      '.thumbnail__image--hover dna-image',
    )
    const swatchData = this.product.colorSwatches[handle]

    // @segment
    Segment.protect(() => {
      Segment.track('Product Card Color Swatch Clicked', {
        category: this.productTitle?.style,
        clicked_swatch: swatchData.productName,
        clicked_swatch_position: this.sortedColorSwatchKeys.indexOf(handle) + 1,
        name: this.productTitle?.color,
        price: Number(minorToMajorUnit(this.rawPrice)),
        product_category: this.product.tags
          ? getCategoryFromTags(this.product?.tags)
          : undefined,
        product_id: this.product.id,
        section_page_position: this.sectionPosition,
        section_tracking_key: this.trackingKey,
        shopify_product_id: this.product.id,
        sku_short:
          getMasterSku(this.product.sku) ||
          getMasterSku(this.product.variants[0].sku),
        url:
          this.product.onlineStoreUrl ||
          `${window.location.origin}/products/${this.product.handle}`,
      })
    })

    this.selectedSwatch = handle
    this.product.title = swatchData.productName
    this.product.handle = swatchData.handle
    this.product.price = swatchData.price
    $primaryImage?.setAttribute('src', swatchData.image[1].url)
    $primaryImage?.setAttribute('alt', swatchData.image[1].alt_text)
    $secondaryImage?.setAttribute('src', swatchData.image[2].url)
    $secondaryImage?.setAttribute('alt', swatchData.image[2].alt_text)
    ;(async () => {
      const swatchProduct = await storefrontClient.getProductByHandle(
        swatchData.handle,
      )
      await this.updateComplete
      const $wishlistButton = this.renderRoot.querySelector(
        '.swymcs-wishlist-button',
      )
      document.dispatchEvent(
        new CustomEvent('dna:plp:option-change', {
          detail: {
            buttonElement: $wishlistButton,
            context: 'plp',
            id: swatchData.productId,
            swymVariantId: swatchProduct.variants[0].id,
            priceRegular: this.product.price,
            tags: swatchProduct.tags,
            title: this.product.title,
          },
        }),
      )
    })()
  }
  // END / TECH-1516

  _trackProductCardClicked = (event) => {
    // Since the whole card is clickable, there are certain elements that we need to disable the click + navigation action for.
    if (
      event.target.closest(CLOSEST_SELECTORS_OF_IGNORED_CLICKS) ||
      event.target.matches(SELECTORS_OF_IGNORED_CLICKS) ||
      this.swatchesScrolling
    ) {
      event.preventDefault()
      return
    }

    // @segment
    Segment.protect(() => {
      const eventName =
        this.closest('sl-carousel-item') || this.isSearch
          ? Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_CLICKED
          : Segment.CONSTANTS.EVENTS.PRODUCT_CLICKED

      Segment.track(eventName, {
        category: this.productTitle?.style,
        name: this.productTitle?.color,
        price: Number(minorToMajorUnit(this.rawPrice)),
        product_carousel_pod_id:
          eventName === Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_CLICKED &&
          this.dataset.cnstrcRecommendationsPodId?.length
            ? this.dataset.cnstrcRecommendationsPodId
            : undefined,
        product_category: this.product.tags
          ? getCategoryFromTags(this.product?.tags)
          : undefined,
        product_click_context: this.isSearch ? 'search' : undefined,
        product_id: this.product.id,
        section_page_position: this.sectionPosition,
        section_tracking_key: this.trackingKey,
        shopify_product_id: this.product.id,
        sku_short:
          getMasterSku(this.product.sku) ||
          getMasterSku(this.product.variants[0].sku),
        type:
          eventName === Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_CLICKED
            ? this.carouselStyle
            : undefined,
        url:
          this.product.onlineStoreUrl ||
          `${window.location.origin}/products/${this.product.handle}`,
      })
    })
  }

  _trackProductCardCarouselChange = () => {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.PRODUCT_IMAGE_SCROLLED, {
        category: this.productTitle?.style,
        name: this.productTitle?.color,
        price: Number(minorToMajorUnit(this.rawPrice)),
        product_category: this.product.tags
          ? getCategoryFromTags(this.product?.tags)
          : undefined,
        product_id: this.product.id,
        shopify_product_id: this.product.id,
        sku_short: getMasterSku(this.product.sku),
        url: this.product.onlineStoreUrl,
      })
    })
  }

  _handleOpenQuickViewModal = () => {
    const updatedVariants = this.product.variants.map((variant) => ({
      ...variant,
      variant_title: variant.title,
    }))
    const product = {
      media: this.product.media,
      title: this.productTitle?.style,
      price: this.product.price?.[Shopify.country]
        ? this.product.price?.[Shopify.country]
        : '',
      compareAtPrice: this.product.compareAtPrices?.[Shopify.country]
        ? this.product.compareAtPrices[Shopify.country] * 100
        : '',
      color: this.productTitle?.color,
      id: this.product.id,
      url: `${window.location.origin}${Shopify.routes.root}products/${this.product.handle}`,
      addOnItemsPresent: this.product.addOnItemsPresent,
      variant_id: this.product.variants[0].id,
      handle: this.product.handle,
      tags: this.product.tags,
      available: this.product.available,
      metafields: this.product.metafields,
      sku: this.product.sku,
      onlineStoreUrl: this.product.onlineStoreUrl,
      variants: updatedVariants,
    }
    document.dispatchEvent(
      new CustomEvent(`dna:quick-view:${this.sectionId}:open`, {
        detail: { product },
      }),
    )
  }

  _renderReviewStars = () => {
    const numStars = this.product?.metafields?.yotpo?.reviews_average
    const reviewCount = this.product?.metafields?.yotpo?.reviews_count
    if (!numStars) {
      return
    }
    const maxStars = 5
    const roundedStars = Math.round(numStars * 2) / 2

    const fullStars = Math.floor(roundedStars)
    const hasHalfStar = roundedStars % 1 === 0.5
    const emptyStars = Math.floor(maxStars - roundedStars)
    const stars = []

    for (let i = 0; i < fullStars; i++) {
      stars.push(html`${fullStar}`)
    }
    if (hasHalfStar) {
      stars.push(html`${halfStar}`)
    }
    for (let i = 0; i < emptyStars; i++) {
      stars.push(html`${emptyStar}`)
    }
    return html`
      <div class="footer__stars">
        <div class="star-container">${stars}</div>
        ${reviewCount ? html`<span class="review-count">${reviewCount}</span>` : ''}
      </div>
    `
  }

  // @intelligems TECH-1516
  _renderFooterSwatches = () => {
    return html`
      <div class="footer__swatches js-footer-swatches">
        ${when(
          this.applyTest1516v2 &&
            this.colorCount > this.swatchFillCount &&
            this.swatchScrollPosition !== 0,
          () => html`
            <div
              class="footer__swatches-prev js-swatch-prev"
              @click=${this._handleSwatchScroll}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
                <path d="M5.20948 0.708497L0.708495 5.20948L5.20947 9.71046" stroke="#03143B" stroke-width="1.00189" stroke-miterlimit="10" stroke-linecap="square"/>
                <path d="M5.20948 0.708497L0.708495 5.20948L5.20947 9.71046" stroke="black" stroke-opacity="0.2" stroke-width="1.00189" stroke-miterlimit="10" stroke-linecap="square"/>
              </svg>
            </div>
          `,
          () => nothing,
        )}
        <div class="fieldset-swatch-container">
          <fieldset class="fieldset-swatch">
            <legend class="legend legend-container sr-only">
              Color
            </legend>
            ${repeat(
              this.sortedColorSwatchKeys,
              (key) => key.handle,
              (key, index) => {
                const swatchName =
                  this.product.colorSwatches[key.handle].productName.split(
                    ' - ',
                  )[1]
                const swatchFilename = `/cdn/shop/files/${handleize(swatchName)}.png${window.ShopifyAssets?.themeJsVersion ? `?v=${window.ShopifyAssets.themeJsVersion}` : ''}`
                return html`
                  <input
                    aria-label=${this.product.colorSwatches[key.handle].productName}
                    class=${classMap({
                      swatch: true,
                      'swatch--checked':
                        this.selectedSwatch ===
                        this.product.colorSwatches[key.handle].handle,
                    })}
                    id=${this.product.colorSwatches[key.handle].handle}
                    key=${this.product.colorSwatches[key.handle].handle}
                    name="color"
                    type="radio"
                    value=${swatchName}
                  />
                  <label
                    class=${classMap({
                      hidden: when(
                        this.swatchScrollPosition === 0,
                        () => index >= this.swatchFillCount || false,
                        () =>
                          index < this.swatchScrollPosition ||
                          index >=
                            this.swatchScrollPosition +
                              (this.swatchFillCount - 1) ||
                          false,
                      ),
                    })}
                    for=${this.product.colorSwatches[key.handle].handle}
                    @click=${(e) => this._handleSwatchClick(e, this.product.colorSwatches[key.handle].handle)}
                  >
                    <img src="${swatchFilename}" loading="eager" alt="" />
                  </label>
                `
              },
            )}
          </fieldset>
          ${when(
            !this.applyTest1516v2,
            () => html`
              <a
                class="footer__swatches-more-colors"
                href=${this.productUrl}
                @click=${() => {
                  document.location = this.productUrl
                }}
              >
                ${when(
                  this.isMobile,
                  () =>
                    when(
                      this.colorCount > 3,
                      () => html`+${this.colorCount - 3}`,
                      () => nothing,
                    ),
                  () =>
                    when(
                      this.colorCount > 4,
                      () => html`+${this.colorCount - 4}`,
                      () => nothing,
                    ),
                )}
              </a>
            `,
            () => nothing,
          )}
        </div>
        ${when(
          this.applyTest1516v2 &&
            this.colorCount > this.swatchFillCount &&
            this.swatchScrollPosition < this.colorCount - this.swatchFillCount,
          () => html`
            <div
              class="footer__swatches-next js-swatch-next"
              @click=${this._handleSwatchScroll}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
                <path d="M0.708493 9.71045L5.20947 5.20947L0.708494 0.70849" stroke="#03143B" stroke-width="1.00189" stroke-miterlimit="10" stroke-linecap="square"/>
                <path d="M0.708493 9.71045L5.20947 5.20947L0.708494 0.70849" stroke="black" stroke-opacity="0.2" stroke-width="1.00189" stroke-miterlimit="10" stroke-linecap="square"/>
              </svg>
            </div>
          `,
          () => nothing,
        )}
      </div>
    `
  }
  // END / TECH-1516

  _renderPrimaryBadge = () => {
    let badgeColor = 'c-midnight'
    if (this.primaryBadgeType?.includes('brand')) {
      badgeColor = 'c-reflex-blue'
    } else if (this.primaryBadge === 'Final Sale') {
      badgeColor = 'c-attention'
    }
    return html`
      ${when(
        this.primaryBadge,
        () => html`
          <div class="badge">
            ${when(
              this.primaryBadgeType?.includes('promotion'),
              () => html`
                <span class="bg-midnight c-white">
                  ${this.primaryBadge}
                </span>
              `,
              () => html`
                <span
                  class="${classMap({
                    'bg-white': true,
                    [badgeColor]: true,
                  })}"
                >
                  ${this.primaryBadge}
                </span>
              `,
            )}
          </div>
        `,
        () => nothing,
      )}
    `
  }

  _renderWishlistIcon = () => {
    return html`<swymcs-wishlist-button class="swymcs-wishlist-button" data-cnstrc-btn="add_to_wishlist">
      <Button
        id="swymcs-wishlist-button"
        class="swymcs-wishlist-button swymcs-wishlist-button-${this.product.id}"
        data-empi="${this.product.id}"
        data-epi="${this.product.variants[0]?.id}"
        data-du="${this.productUrl}"
        data-price="${this.rawPrice}"
        data-product-category="${getCategoryFromTags(this.product.tags)}"
        data-title="${this.product.title}">
        <div
          id="swymUnfilled" 
          style="height: 100%; display: flex; justify-content: center; align-items: center;"
        >
        ${
          this.isMobile
            ? html`
            <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.4056 1.10919C9.03827 1.10919 8.00488 2.66055 8.00488 2.66055C8.00488 2.66055 6.95165 0.760339 4.31797 1.20883C2.57485 1.50566 1.22247 2.66055 1.0256 4.57305C0.685006 7.88175 2.83737 11.1866 7.55367 13.9773C7.79817 14.0566 7.90959 14.0855 8.02664 14.0855C8.1437 14.0855 8.29862 14.0632 8.3954 13.9973C10.9467 12.3244 15.0827 9.18859 15.0429 5.20188C15.0232 3.28127 13.5792 1.17055 11.4056 1.10919Z" stroke="white" stroke-width="1.00189" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M11.0549 2.07672C9.0262 2.07672 8.0262 4.07672 8.0262 4.07672C8.0262 4.07672 7.0262 2.07672 4.99745 2.07672C3.3487 2.07672 2.04307 3.4561 2.0262 5.10203C1.99182 8.5186 4.73651 10.9483 7.74495 12.9902C7.82789 13.0466 7.92588 13.0768 8.0262 13.0768C8.12651 13.0768 8.22451 13.0466 8.30745 12.9902C11.3156 10.9483 14.0603 8.5186 14.0262 5.10203C14.0093 3.4561 12.7037 2.07672 11.0549 2.07672Z" stroke="#03143B" stroke-width="1.00189" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M11.0549 2.07672C9.0262 2.07672 8.0262 4.07672 8.0262 4.07672C8.0262 4.07672 7.0262 2.07672 4.99745 2.07672C3.3487 2.07672 2.04307 3.4561 2.0262 5.10203C1.99182 8.5186 4.73651 10.9483 7.74495 12.9902C7.82789 13.0466 7.92588 13.0768 8.0262 13.0768C8.12651 13.0768 8.22451 13.0466 8.30745 12.9902C11.3156 10.9483 14.0603 8.5186 14.0262 5.10203C14.0093 3.4561 12.7037 2.07672 11.0549 2.07672Z" stroke="black" stroke-opacity="0.2" stroke-width="1.00189" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            `
            : html`
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.8877 2.24158C18.157 2.24163 19.4347 2.91568 20.4082 3.78357C21.378 4.64821 22.1475 5.79686 22.3027 6.86951L22.3457 7.18982C22.7402 10.4818 21.7769 13.0836 20.0996 15.3011C18.6005 17.2831 16.5274 18.9617 14.3701 20.5648L13.4424 21.2474C13.3614 21.3067 13.1703 21.4414 12.9199 21.556C12.6843 21.6639 12.3566 21.7708 11.998 21.7504V21.7513C11.4439 21.7513 10.991 21.5322 10.7334 21.3744L10.5449 21.2474L10.5273 21.2338C5.40488 17.131 1.50293 14.2615 1.50293 8.16248L1.50879 7.90759C1.63731 5.27054 3.74638 2.65372 7.03516 2.27576L7.06641 2.27283C8.73156 2.18637 10.0672 2.81877 10.9727 3.45544C11.4014 3.75691 11.7377 4.06421 11.9756 4.30408C12.1847 4.07425 12.4786 3.78896 12.8652 3.50525C13.7323 2.86901 15.0564 2.24158 16.8877 2.24158Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16.5417 3.2511C19.3073 3.25129 21.4699 5.56018 21.4978 8.2843L21.491 8.78723C21.2805 13.9363 17.0594 17.5768 12.7009 20.5353L12.7 20.5343C12.4928 20.675 12.2483 20.7511 11.9978 20.7511C11.7473 20.751 11.5028 20.6752 11.2957 20.5343V20.5353C6.79614 17.4814 2.44442 13.7008 2.49878 8.2843L2.50757 8.02942C2.66266 5.41757 4.77586 3.25127 7.45483 3.2511C9.15361 3.2511 10.4093 4.09125 11.2205 4.89661C11.5457 5.21952 11.8038 5.5422 11.9978 5.81262C12.1918 5.54211 12.4507 5.21971 12.7761 4.89661C13.5873 4.09125 14.843 3.2511 16.5417 3.2511Z" stroke="#03143B" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16.5417 3.2511C19.3073 3.25129 21.4699 5.56018 21.4978 8.2843L21.491 8.78723C21.2805 13.9363 17.0594 17.5768 12.7009 20.5353L12.7 20.5343C12.4928 20.675 12.2483 20.7511 11.9978 20.7511C11.7473 20.751 11.5028 20.6752 11.2957 20.5343V20.5353C6.79614 17.4814 2.44442 13.7008 2.49878 8.2843L2.50757 8.02942C2.66266 5.41757 4.77586 3.25127 7.45483 3.2511C9.15361 3.2511 10.4093 4.09125 11.2205 4.89661C11.5457 5.21952 11.8038 5.5422 11.9978 5.81262C12.1918 5.54211 12.4507 5.21971 12.7761 4.89661C13.5873 4.09125 14.843 3.2511 16.5417 3.2511Z" stroke="black" stroke-opacity="0.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`
        }
        </div>
      </Button>
    </swymcs-wishlist-button>
  `
  }

  _handleHover = (event) => {
    document.dispatchEvent(
      new CustomEvent('dna:product-card:hover', {
        bubbles: true,
        detail: {
          product: this.product,
          url: this.productUrl,
        },
      }),
    )
  }

  _handleThumbnailScroll = (event) => {
    const scrollThreshold = this.$thumbnail.value.offsetWidth / 2.1
    if (this.$thumbnail.value.scrollLeft >= scrollThreshold) {
      if (this.thumbnailScroll !== 'right') {
        this.thumbnailScroll = 'right'
        this._trackProductCardCarouselChange()
      }
    } else {
      if (this.thumbnailScroll !== 'left') {
        this.thumbnailScroll = 'left'
        this._trackProductCardCarouselChange()
      }
    }
  }

  _renderWithProductLink(content) {
    return html`
      <a
        ?apply-test-1516=${this.applyTest1516}
        ?apply-test-1516-v2=${this.applyTest1516v2}
        class="product-card-item__link js-product-card-link"
        @mouseenter=${this._handleHover}
        @click=${(e) => this._trackProductCardClicked(e)}
        href=${this.productUrl}
        aria-label="${t('accessibility.view_details')} ${t('accessibility.for')} ${this.productTitle.style} ${this.productTitle.color}">
          ${content()}
      </a>
    `
  }

  _renderFeaturedImage = () => {
    const featuredImage = this.editorialImage
      ? this.editorialImage
      : this.productMedia[0]
    return html`
      <div class="thumbnail__image thumbnail__image--featured ${this.editorialImage ? 'thumbnail__image--editorial' : ''}">
        <dna-image
          src=${featuredImage?.url}
          alt=${removeTextBeforeCharacterAndTrim(featuredImage?.alt, '|')}
          width="100%"
          height=${this.editorialImage ? '100%' : 'auto'}
          loading=${this.imageLoading ? this.imageLoading : 'lazy'}
          additional-styles="object-fit: cover;"
          sizes="(max-width: 768px) 260px, (min-width: 768px) 600px"
          fetch-priority=${this.fetchPriority ? this.fetchPriority : 'low'}
        />
      </div>
    `
  }

  _renderHoverImage = () => {
    const hoverImage = this.editorialImage
      ? this.productMedia[0]
      : this.productMedia[1]
    return html`
      <div class="thumbnail__image thumbnail__image--hover ${this.editorialImage ? 'thumbnail__image--hover-editorial' : ''}">
        <dna-image
          src=${hoverImage.url}
          alt=${removeTextBeforeCharacterAndTrim(hoverImage.alt, '|')}
          width="100%"
          height="100%"
          fit="cover"
          loading=${this.imageLoading}
          sizes="(max-width: 768px) 260px, (min-width: 768px) 600px"
        />
      </div>
    `
  }

  _renderThumbnail = () => {
    let scrolling = false
    return html`
      <div
        ${ref(this.$thumbnail)}
        @scroll=${(e) => {
          if (!scrolling) {
            window.requestAnimationFrame(() => {
              this._handleThumbnailScroll(e)
              scrolling = false
            })
            scrolling = true
          }
        }}
        class=${classMap({
          thumbnail: true,
          'thumbnail--mobile': this.isMobile,
          'thumbnail--no-hover': !this.showHoverImage,
          'thumbnail--compact': this.compactCard,
        })}
      >
        ${this._renderFeaturedImage()}
        ${when(
          this.showHoverImage && !this.isMobile,
          () => this._renderHoverImage(),
          () => nothing,
        )}
        ${when(
          this.showHoverImage && this.showCarousel && this.isMobile,
          () => this._renderHoverImage(),
          () => nothing,
        )}
        ${when(
          !this.isGiftCard && this.quickViewEnabled && !this.isMobile,
          this._renderQuickView,
          () => nothing,
        )}
      </div>
      ${when(
        this.isMobile && this.showCarousel && this.showHoverImage,
        () => html`
          <span
            class=${classMap({
              'js-card-carousel-indicator': true,
              'thumbnail__pagination-indicator': true,
              'thumbnail__pagination-indicator--left':
                this.thumbnailScroll === 'left',
              'thumbnail__pagination-indicator--right':
                this.thumbnailScroll === 'right',
            })}
          ></span>
        `,
        () => nothing,
      )}
    `
  }

  _renderQuickView = () => {
    return html`
    <div class="quick-view-container">
      <div class="quick-view-cta">
        <a @click=${(e) => {
          this._handleOpenQuickViewModal(e)
        }} role="button" aria-label="${t('sections.quick_view.aria_label')}">${t('sections.quick_view.cta_label')}</a>
      </div>
    </div>
    `
  }

  _renderHeader() {
    return html`
      <div class="header">
        <div class=${classMap({
          'badge-wrapper': true,
          'badge-wrapper--short': this.isSearch,
        })}">
          ${when(
            this.primaryBadge && !this.hideBadges,
            () => html`
              ${this._renderPrimaryBadge()}
            `,
            () => nothing,
          )}
        </div>
        ${when(this.hideWishlistIcon, () => nothing, this._renderWishlistIcon)}
      </div>
    `
  }

  _renderFooter() {
    const showSecondaryBadge =
      this.secondaryBadge && !this.hideBadges && !this.compactCard
    const showColorText = this.productTitle.color && !this.hideProductColorName
    return html`
      <div class=${classMap({
        footer: true,
        'footer--us': this.usPricing,
        'footer--no-secondary-badge': !showSecondaryBadge,
        'footer--search': this.isSearch,
        'footer--compact': this.compactCard,
      })}>
        <!-- @intelligems TECH-1516 -->
        ${when(
          this.applyTest1516 && !this.applyTest1516v2,
          () => this._renderFooterSwatches(),
          () => nothing,
        )}
        <!-- END TECH-1516 -->
        <div class="info" data-product-handle=${this.product.handle}>
          ${when(
            !this.compactCard,
            () => html`
              <div class="title">
                ${when(
                  this.productTitle.style,
                  () => html`
                    <h3 class="p3" role="presentation"><strong>${this.productTitle.style}</strong></h3>
                  `,
                  () => nothing,
                )}
                ${when(
                  this.showReviewStars,
                  () => this._renderReviewStars(),
                  () => nothing,
                )}
                ${when(
                  showColorText,
                  () =>
                    html`<small class="footer__color">${this.productTitle.color}</small>`,
                  () => nothing,
                )}
                ${when(
                  this.colorCount &&
                    !this.hideProductColorCount &&
                    this.applyTest1516v2,
                  () => html`
                    <div class="footer__count">${this.colorCount} ${this.colorCount > 1 ? 'Colors' : 'Color'}</div>
                  `,
                  () => nothing,
                )}
              </div>
            `,
            () => nothing,
          )}
          <div
            class="price p3 
              ${this.renderedComparePrice ? 'price--markdown' : ''}
              ${this.isSearch ? 'price--narrow' : ''}"
            ?data-hype-available=${this.product.available}
          >
            ${when(
              !!this.renderedComparePrice,
              () => html`<s>${this.renderedComparePrice}</s>`,
              () => nothing,
            )}
            ${this.renderedPrice}
          </div>
        </div>
        ${when(
          showSecondaryBadge,
          () => html`
            <div class="badge2 p4">
              ${this.secondaryBadge}
            </div>
          `,
          () => nothing,
        )}
        <!-- @intelligems TECH-1516 -->
        ${when(
          this.applyTest1516v2,
          () => this._renderFooterSwatches(),
          () => nothing,
        )}
        <!-- END TECH-1516 -->
        ${when(
          !this.isGiftCard && this.quickViewEnabled && this.isMobile,
          this._renderQuickView,
          () => nothing,
        )}
      </div>
    `
  }

  render() {
    return this._renderWithProductLink(
      () => html`
        ${when(
          !this.compactCard,
          () => this._renderHeader(),
          () => nothing,
        )}
        ${this._renderThumbnail()}
        ${this._renderFooter()}
      `,
    )
  }

  static styles = [
    styles.a11y,
    styles.color,
    styles.flex,
    styles.typography,
    css`
      :host {
        background-color: var(--dna-color-sandstone);
        color: var(--dna-color-midnight);
        border: 1px solid #03143B40;
        border-top: 0;
        border-left: 0;
        border-right-color: var(--dna-brand-reverse);
        border-bottom-color: var(--dna-brand-reverse);
        display: flex;
        flex-direction: column;
        flex: 1 0 25%;
        position: relative;
        height: 100%;
        width: 100%;
        overflow: hidden;
      }
      :host([is-search="true"]) {
        display: block;
        border: 1px solid var(--dna-brand-reverse);
      }
      h3 {
        margin: 0;
      }
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }
      .badge-wrapper {
        height: var(--dna-space-8);
        width: 100%;
      }
      .badge {
        padding: var(--dna-space-2);
        span {
          display: inline-block;
          padding: 5px var(--dna-space-2);
        }
      }
      .badge2 {
        border: thin solid var(--dna-color-midnight);
        display: inline-block;
        font-size: 11px;
        line-height: 1;
        margin-top: var(--dna-space-3);
        padding: var(--dna-space-1) var(--dna-space-2);
      }
      :host([is-search="true"]) .badge {
        padding: var(--dna-space-2);

        span {
          overflow-x: hidden;
          white-space: nowrap;
          max-width: 66%;
          text-overflow: ellipsis;
        }
      }
      .price {
        display: flex;
        justify-content: flex-end;
        justify-content: left;
        gap: var(--dna-space-2);
      }
      .price--markdown {
        color: var(--dna-utility-attention);
      }
      .price--markdown s {
        color: var(--dna-color-midnight);
      }
      .thumbnail {
        aspect-ratio: 3 / 4;
        display: flex;
        height: 100%;
        width: 100%;
        position: relative;
        transition: opacity 0.5s ease;
      }
      .thumbnail--mobile {
        overflow-x: auto;
        overflow-y: hidden;
        scroll-behavior: smooth;
        scroll-snap-type: x mandatory;
        height: auto;
      }
      .thumbnail--mobile::-webkit-scrollbar {
        display: none;
        scrollbar-width: none;
      }
      .thumbnail--compact {
        aspect-ratio: 9 / 10;
      }
      .card-image-link {
        display: block;
        height: 100%;
      }
      .thumbnail__image {
        scroll-snap-align: start;
        display: block;
        flex-shrink: 0;
        transition: opacity .5s ease;
        height: 100%;
        width: 100%;
      }
      .thumbnail__image--featured {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      .thumbnail__image--hover {
        pointer-events: none;
      }
      .thumbnail__image--hover a {
        pointer-events: all;
      }
      .thumbnail__pagination-indicator {
        position: relative;
        border-top: thin solid var(--dna-brand-primary);
        height: 0;
        width: calc(50% - var(--dna-space-2));
        transition: transform .2s ease;
      }
      .thumbnail__pagination-indicator--left {
        transform: translateX(var(--dna-space-2));
      }
      .thumbnail__pagination-indicator--right {
        transform: translateX(calc(100% + var(--dna-space-2)));
      }
      .thumbnail__image--editorial {
        display: block;
      }
      .thumbnail__image--hover-editorial {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      @media screen and (min-width: 1025px) {
        .thumbnail__image--hover {
          position: absolute;
          top: 0;
          opacity: 0;
        }
      :host(:focus-visible) .thumbnail:not(.thumbnail--no-hover) .thumbnail__image--featured,
        :host(:hover) .thumbnail:not(.thumbnail--no-hover) .thumbnail__image--featured {
          opacity: 0;
          pointer-events: none;
        }
        :host(:focus-visible) .thumbnail:not(.thumbnail--no-hover) .thumbnail__image--hover,
        :host(:hover) .thumbnail__image--hover {
          opacity: 1;
        }
        .thumbnail__image--editorial {
          display: block;
          top: 0;
          position: absolute;
        }
      }
      .product-card-item__link {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        color: inherit;
        text-decoration: inherit;
      }
      status-favorite-button {
        margin-right: var(--dna-space-3);
        margin-top: var(--dna-space-3);
        --icon-stroke-filled: var(--dna-color-midnight);
        --icon-fill-filled: var(--dna-color-midnight);
        --icon-background: transparent;
        --icon-shadow-color: transparent;
        --icon-size: 16px;
        position: absolute;
        top: 0;
        right: 0;
        padding-left: 10px;
        padding-bottom: 10px;
        z-index: var(--dna-z-index-content);
        visibility: hidden;
      }
      @media screen and (max-width: 1024px) {
        .badge-wrapper {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 1;
        }
        status-favorite-button {
          margin-right: 0;
          margin-top: 0;
        }
        status-favorite-button::part(button) {
          padding-left: 5px;
          padding-bottom: 5px;
        }
      }
      status-favorite-button::part(button) {
        padding-top: var(--dna-space-4);
        padding-right: var(--dna-space-4);
      }
      @media screen and (max-width: 1024px) {
        status-favorite-button::part(button) {
          padding-top: var(--dna-space-2);
          padding-right: var(--dna-space-2);
        }
      }
      status-favorite-button::part(icon-wrapper) {
        width: unset;
        height: unset;
      }
      status-favorite-button.is-active {
        visibility: visible;
      }
      .quick-view-cta {
        font: var(--dna-body-p3-font);
        font-size: 11px;
        text-transform: uppercase;
        text-decoration: underline;
        letter-spacing: 2px;
        margin-top: 16px;
      }
      @media screen and (min-width: 1025px) {
        .quick-view-cta {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 0;
          overflow: hidden;
          transition: height 0.3s ease;
          background-color: transparent;
          color: var(--dna-color-midnight);
          z-index: 10;
          text-align: center;
          align-items: center;
          margin-top: 14px;
          justify-content: center;
          font-weight: 500;
          background-color: rgba(244, 242, 239, 0.5);
        }
        .quick-view-container {
          margin-top: 0;
        }
        :host(:hover) .quick-view-cta {
          height: 46px;
          display: flex;
        }
      }
      .quick-view-cta a {
        cursor: pointer;
      }
      .footer {
        background-color: var(--dna-color-sandstone);
        flex: 1 1 auto;
        padding: var(--dna-space-2);
        position: relative;
      }
      .footer__color {
        font: var(--dna-body-p3-font);
      }
      .footer__count {
        font: var(--dna-body-p3-font);
      }
      .footer__stars {
        display: flex;
        margin-top: 4px;
        margin-bottom: 4px;

        .star-container {
          display: flex;
          align-items: center;
        }
        
        .review-count {
          font-size: 12px;
          margin-left: 8px;
        }
      }
      @media screen and (max-width: 1024px) {
        .footer {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: space-between;
        }
        .footer--compact {
          align-items: center;
          padding-top: 0;
        }
        .footer--compact .price {
          gap: var(--dna-space-1);
        }
        .footer__stars {
          margin-top: 2px;
          margin-bottom: 2px;

          svg {
            height: 12px;
            width: 12px;
          }

          .reviews-count {
            font-size: 10px;
            line-height: 10px;
            margin-top: 1px;
          }
        }
      }
      /* breakpoint-small desktop for search adjustments */
      @media screen and (min-width: 1024px) and (max-width: 1155px) {
        .footer--us.footer--search .price {
          min-width: 50px;
        }
      }
      /* breakpoint-desktop */
      @media screen and (min-width: 1024px) {
        .header {
          position: absolute;
          top: 0;
          z-index: var(--dna-z-index-content);
        }
        .badge {
          padding: var(--dna-space-4);
          z-index: var(--dna-z-index-content);

          span {
            padding: 5px var(--dna-space-2);
          }
        }
        :host([is-search="true"]) .badge {
          padding: var(--dna-space-2);

          span {
            overflow-x: hidden;
            white-space: nowrap;
            max-width: 74%;
            text-overflow: ellipsis;
          }
        }
        .badge2 {
          font: var(--dna-body-p3-font);
          line-height: 14px;
        }
        .price {
          display: flex;
          flex-direction: unset;
        }
        .badge-wrapper {
          height: 60px;
        }
        .badge-wrapper--short {
          height: 46px;
        }
        :host([is-search="true"]) .badge-wrapper {
          height: 30px;
        }
        .footer {
          padding: var(--dna-space-3) var(--dna-space-4) var(--dna-space-4) var(--dna-space-4);
        }
        .footer.footer--search {
          padding: calc(var(--dna-space-3) / 2) var(--dna-space-2) var(--dna-space-2);
        }
        .footer.footer--compact {
          padding: var(--dna-space-2) var(--dna-space-4) var(--dna-space-2) var(--dna-space-4);
        }
        .footer--us .price {
          min-width: 80px;
          text-align: right;
          justify-content: right;
        }
        .footer--us.footer--compact .price {
          justify-content: center;
        }
        .footer--us .info {
          display: flex;
          justify-content: space-between;
          gap: var(--dna-space-2);
        }
        .footer.footer--compact .info {
          justify-content: center;
        }
        .thumbnail {
          height: unset;
          padding-bottom: 0;
        }
        :host(.product-card__product-carousel) .thumbnail:not(.thumbnail--compact) {
          aspect-ratio: .82;
        }
        .footer .p3 {
          font: var(--dna-body-p2-font);
        }
        .footer__color {
          font: var(--dna-body-p2-font);
        }
        .footer__count {
          font: var(--dna-body-p2-font);
        }
        :host #swymcs-wishlist-button {
          width: var(--dna-space-10); 
          height: var(--dna-space-10); 
          padding-top: var(--dna-space-2);
        }
      }
      :host #swymcs-wishlist-button {
        cursor: pointer !important;
        position: absolute;
        z-index: 5;
        top: 0;
        right: 0;
        padding: var(--dna-space-2); 
        font-size: var(--dna-space-4); 
        cursor: pointer; 
        opacity: 1 !important; 
        color: #ffffff; 
        background: none; 
        border: unset; 
        display: flex; 
        justify-content: center;
        padding-top: 10px;
      }
      @media screen and (min-width: 1024px) {
        :host #swymcs-wishlist-button {
          top: calc(var(--dna-space-5) / 2);
          right: calc(var(--dna-space-5) / 2);
        }
        :host([is-search="true"]) #swymcs-wishlist-button {
          top: var(--dna-space-0-5);
          right: 0;
        }
      }

      /* @intelligems TECH-1516 */
      [apply-test-1516] {
        .footer__swatches {
          display: flex;
          align-items: center;
          gap: var(--dna-space-4);
          margin: var(--dna-space-3) 0;

          @media screen and (min-width: 1024px) {
            margin-top: 0;
          }
        }
        .fieldset-swatch-container {
          display: flex;
          align-items: center;
          gap: calc(var(--dna-space-2) - 1px);
          overflow-x: hidden;
        }
        .fieldset-swatch {
          display: flex;
          align-items: center;
          gap: var(--dna-space-4);
          border: none;
          height: calc(var(--dna-space-7) + var(--dna-space-0-5));
          margin: 0;
          padding: 0;
          padding-left: 3px;
          padding-right: 3px;

          @media screen and (min-width: 1024px) {
            gap: var(--dna-space-3);
            height: calc(var(--dna-space-6) + var(--dna-space-0-5));
          }
        }
        input[type='radio'].swatch {
          clip: rect(0, 0, 0, 0);
          overflow: hidden;
          position: absolute;
          height: 1px;
          width: 1px;
        }
        input[type='radio'].swatch + label {
          display: block;
          cursor: pointer;
          background-color: transparent;

          img {
            display: block;
            border: 1px solid var(--dna-color-light-grey);
            border-radius: 100%;
            box-sizing: border-box;
            height: var(--dna-space-6);
            outline: 1px solid transparent;
            outline-offset: var(--dna-space-0-5);
            width: var(--dna-space-6);

            @media screen and (min-width: 1024px) {
              height: var(--dna-space-5);
              width: var(--dna-space-5);
            }
          }
        }
        input[type='radio'].swatch--checked + label img {
          outline: 1px solid var(--dna-brand-primary);
        }
        .footer__swatches-more-colors {
          color: var(--dna-brand-primary);
          text-decoration: none;
        }
        @media screen and (max-width: 1024px) {
          .footer {
            justify-content: unset;
          }
        }
        @media screen and (min-width: 1024px) {
          input[type='radio'].swatch:hover + label img {
            outline: 1px solid var(--dna-brand-primary);
            outline-offset: var(--dna-space-0-5);
          }
        }
      }
      [apply-test-1516-v2] {
        .footer {
          display: flex;
          flex-direction: column;
          justify-content: space-between
        }
        .badge2 {
          align-self: flex-start;
        }
        .footer__swatches {
          gap: var(--dna-space-4);
          margin: var(--dna-space-3) 0 0;
          width: 100%;

          @media screen and (min-width: 1024px) {
            gap: var(--dna-space-3);
            margin: 0;
            margin-top: var(--dna-space-3);
          }
        }
        .fieldset-swatch-container {
          gap: var(--dna-space-5);

          @media screen and (min-width: 1024px) {
            gap: var(--dna-space-3);
          }
        }
        .footer__swatches-next,
        .footer__swatches-prev {
          display: flex;
          align-items: center;
          flex: 0 0 auto;
          justify-content: center;
          border: 1px solid var(--dna-color-light-grey);
          border-radius: 100%;
          box-sizing: border-box;
          height: calc(var(--dna-space-6) + var(--dna-space-1));
          width: calc(var(--dna-space-6) + var(--dna-space-1));

          @media screen and (min-width: 768px) {
            height: var(--dna-space-6);
            width: var(--dna-space-6);
          }
        }
      }
      /* END TECH-1516 */
    `,
  ]
}

customElements.define('dna-product-card', DNAProductCard)
